import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { editOnsiteInterview } from '~/actions/PermanentJobs';
import {
  BackButton,
  Box,
  Center,
  Column,
  CustomSpinner,
  FloatingContainer,
  Row,
  Screen,
  Text,
} from '~/commonComponents';
import { EnforceUpdateInterviewRequest } from '~/constants/ff.posthog';
import { useAppSelector } from '~/hooks';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import {
  TOnsiteInterviewFormData,
  OnsiteInterviewForm,
  OnsiteInterviewFormFields,
} from '../components';
import { useGetDraftJobPost } from '~/domain/PermanentJobs';
import { showErrorMessage } from '~/domain/utils';
import { ProfessionTypeEnum } from '~/models';
import { EditOnsiteInterviewPageV2 } from './EditOnsiteInterviewPageV2';

type TRouteParams = {
  postingId: string;
  onsiteInterviewId: string;
};

export const EditOnsiteInterviewPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { postingId, onsiteInterviewId } = useParams<TRouteParams>();

  const interviews = useAppSelector((state) => state.permanentJobs.onsiteInterviews);

  const { data: draftData, isLoading, isError, error } = useGetDraftJobPost(postingId);

  const isInterviewRequestRedesign = useFeatureFlag(EnforceUpdateInterviewRequest);

  const { officeName, title, subProfession, professionType, id } = draftData || {};

  useEffect(() => {
    if (isError) {
      showErrorMessage({ error });
    }
  }, [isError, error, dispatch]);

  const defaultValues = useMemo(() => {
    if (!postingId || !onsiteInterviewId) return undefined;

    const currentInterview = interviews[postingId]?.find((it) => it.id === onsiteInterviewId);

    if (!currentInterview) return undefined;

    return {
      breakTime: currentInterview.breakTime,
      dates: [new Date(currentInterview.date)],
      hourlyRate: currentInterview.hourlyRate,
      hoursArrival: currentInterview.hoursArrival,
      hoursDeparture: currentInterview.hoursDeparture,
      isAssistedHygiene: currentInterview.isAssistedHygiene,
      isPaidBreakTime: currentInterview.isPaidBreakTime,
      profession: currentInterview.profession,
      specialty: currentInterview.specialty,
    } as TOnsiteInterviewFormData;
  }, [interviews, postingId, onsiteInterviewId]);

  const handleOnSubmit = (formData: TOnsiteInterviewFormData) => {
    if (!postingId || !onsiteInterviewId) return;

    dispatch(
      editOnsiteInterview(
        {
          breakTime: formData.breakTime,
          hourlyRate: formData.hourlyRate,
          hoursArrival: formData.hoursArrival,
          hoursDeparture: formData.hoursDeparture,
          id: onsiteInterviewId,
          isAssistedHygiene: formData.isAssistedHygiene,
          isPaidBreakTime: formData.isPaidBreakTime,
          profession: formData.profession,
          specialty: formData.specialty,
        },
        postingId,
      ),
    );

    navigate(-1);
  };

  if (isInterviewRequestRedesign && !isLoading && defaultValues) {
    return (
      <EditOnsiteInterviewPageV2
        defaultValues={defaultValues}
        jobTitle={title || ''}
        office={officeName || ''}
        profession={subProfession || professionType || ProfessionTypeEnum.RDH}
        onBack={() => navigate(-1)}
        onSubmit={handleOnSubmit}
      />
    );
  }

  return (
    <Screen>
      <FloatingContainer borderRadius={4} paddingBlock={48} paddingInline={16}>
        <Row position="relative" alignItems="center" justifyContent="center">
          <Box position="absolute" left={0}>
            <BackButton />
          </Box>
          <Text bold fontSize={32} textAlign="center">
            Edit Onsite Interview
          </Text>
        </Row>
        {isLoading ? (
          <Center marginBlock={48}>
            <CustomSpinner isLoading size={24} />
          </Center>
        ) : (
          <Column marginInline="auto" marginTop={40} width={680}>
            <OnsiteInterviewForm
              defaultValues={defaultValues}
              isEdit
              jobId={id || ''}
              jobTitle={title || ''}
              office={officeName || ''}
              profession={subProfession || professionType || ProfessionTypeEnum.RDH}
              readonlyFields={[OnsiteInterviewFormFields.Dates]}
              unavailableDates={defaultValues?.dates}
              onSubmit={handleOnSubmit}
            />
          </Column>
        )}
      </FloatingContainer>
    </Screen>
  );
};
